/**
 * SIG-LEGIBILITY-1 (2026-08-26) — export a signature pad cropped to its ink.
 *
 * WHY THIS EXISTS
 * ---------------
 * A real executed agreement came back with a signature roughly 12mm x 2.5mm on
 * the page, sitting in a white box that punched through the document's cream
 * background. Pulling the image out of that PDF showed the cause: the pad
 * exported 770x540 px of which the ink occupied 463x97 — 89% blank — and the
 * blank part was opaque white.
 *
 * The stamp scales the WHOLE exported canvas to fit its field box, so all that
 * empty margin is what the box gets filled with and the ink shrinks with it.
 * Cropping here is what makes the signature legible on the document.
 *
 * It also stops us storing 89% padding in `envelope.fields[].value`, in
 * `saved_signatures`, and in every loan application's declaration block — see
 * the DISK-1 incident for what unbounded document growth costs.
 *
 * WHY NOT react-signature-canvas's getTrimmedCanvas()
 * --------------------------------------------------
 * It was dropped in the 1.1.0-alpha line this repo pins, and in the 1.0.x
 * versions where it exists it mutates the live canvas — which would wipe the
 * signature the user is still looking at.
 *
 * PAIRS WITH THE BACKEND
 * ----------------------
 * `backend/services/signature_image.py` does the same crop server-side, and
 * additionally keys out a baked-in white background. That is deliberately
 * belt-and-braces, not duplication: signatures already saved under the old
 * behaviour live in `saved_signatures` and get reused on the next envelope, so
 * the executed PDF has to be able to fix them without the signer re-drawing.
 */

/**
 * Crop a signature pad canvas to its ink and return a PNG data URL.
 *
 * Reads the alpha channel only. The pads no longer set `backgroundColor`, so
 * untouched pixels are genuinely transparent and alpha IS the ink mask. If a
 * future change reintroduces an opaque background, no transparent pixel is
 * found, this returns the full canvas unchanged, and the backend normaliser
 * still recovers it — degrading rather than losing the signature.
 *
 * @param {HTMLCanvasElement} canvas raw pad canvas (SignatureCanvas.getCanvas())
 * @returns {string} PNG data URL, cropped when possible; "" for a null canvas
 */
export function trimmedSignatureDataUrl(canvas) {
  if (!canvas || !canvas.width || !canvas.height) return "";
  const full = canvas.toDataURL("image/png");
  let data;
  try {
    data = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height).data;
  } catch (e) {
    // getImageData throws on a tainted canvas. Nothing cross-origin is ever
    // drawn on a signature pad, but an unreadable canvas must not cost the
    // signature — fall back to the untrimmed export.
    return full;
  }

  let minX = canvas.width;
  let minY = canvas.height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < canvas.height; y += 1) {
    for (let x = 0; x < canvas.width; x += 1) {
      // index 3 of each RGBA quad is alpha
      if (data[(y * canvas.width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return full; // no ink found — nothing to crop to

  // A few device pixels of breathing room so a stroke isn't clipped flush at
  // the edge of the exported image.
  const pad = 4;
  const left = Math.max(0, minX - pad);
  const top = Math.max(0, minY - pad);
  const right = Math.min(canvas.width - 1, maxX + pad);
  const bottom = Math.min(canvas.height - 1, maxY + pad);
  const w = right - left + 1;
  const h = bottom - top + 1;

  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  out.getContext("2d").drawImage(canvas, left, top, w, h, 0, 0, w, h);
  return out.toDataURL("image/png");
}

/**
 * Convenience wrapper for a react-signature-canvas ref.
 * Returns "" when the ref is missing or the pad is empty, so callers can keep
 * their existing "did they actually sign?" check.
 *
 * @param {object} ref a SignatureCanvas instance (not the React ref object)
 */
export function trimmedSignatureFromPad(ref) {
  if (!ref || typeof ref.getCanvas !== "function") return "";
  if (typeof ref.isEmpty === "function" && ref.isEmpty()) return "";
  return trimmedSignatureDataUrl(ref.getCanvas());
}
